import PropTypes from 'prop-types';
import React from 'react';
import Kalendae from 'kalendae';

import i18n from '../../i18n';
import CalendarUtils from '../../utils/CalendarUtils';
import InputCalendar from './InputCalendar';

export default class InputSingleCalendar extends InputCalendar {
    static propTypes = {
        ...InputCalendar.propTypes,
        inputType: PropTypes.oneOf(['start', 'end']).isRequired
    };

    componentDidMount() {
        this.drawCalendar();
        this.escapeKeyHandler();

        // When the Calendar is extended to be used in a form
        if (this.checkValidity) {
            this.checkValidity();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.availabilities !== this.props.availabilities ||
            (nextProps.startOn && !nextProps.startOn.isSame(this.props.startOn)) ||
            (nextProps.endOn && !nextProps.endOn.isSame(this.props.endOn))
        ) {
            this.setDateClassMap(nextProps);
        }
        if (nextProps.nbMonths !== this.props.nbMonths) {
            this.setState({
                nbMonths: nextProps.nbMonths
            });
        }
    }

    /**
     * Draws calendar using Kalendae
     */
    customDrawCalendar(kalendaeOptions) {
        // Add popup mode
        const selectedDate = this.props.inputType === 'start' ? this.props.startOn : this.props.endOn;
        kalendaeOptions = Object.assign(kalendaeOptions, {
            popupMode: true,
            selected: selectedDate,
            side: this.props.side
        });

        let isDisplayed = false;
        if (this.calendar) {
            // We need to refresh the calendar, the only solution is to destroy it and show it again
            if ($(this.calendar.container).is(':visible')) {
                isDisplayed = true;
            }
            this.calendar.destroy();
        }

        this.calendar = new Kalendae.Input(this.inputRef.current, kalendaeOptions);

        if (isDisplayed) {
            this.calendar.show();
        }

        this.addCalendarLabel();
    }

    /**
     * Prevent some dates from being selected
     * @param  {Moment} date clicked date
     * @return {bool} true if the date is ok, false while prevent selection
     */
    dateClickedHandler(date) {
        // Midnight
        date.hour(0);

        if (this.props.inputType === 'end' && this.props.startOn) {
            // We are going to select the end date
            if (date.isSame(this.props.startOn)) {
                // Start date = end date
                return false;
            }

            // Don't allow for selections overlapping booked periods
            const range = moment.range(this.props.startOn, date);
            const overlapsBookedPeriod = CalendarUtils.overlapsBookedPeriod(range, this.props.availabilities);

            if (overlapsBookedPeriod) {
                return false;
            }
        }

        if (this.props.inputType === 'start') {
            if (CalendarUtils.dayJustBeforeUnavailablePeriod(date, this.props.availabilities)) {
                // We can't select a start date just before an unavailable period
                return false;
            }
        }

        return true;
    }

    /**
     * Compute blackout based on availabilities
     * @return {Array} Array of blackout dates
     */
    getBlackout() {
        const blackout = super.getBlackout();

        let value;
        if (this.props.inputType === 'start') {
            value = this.props.startOn;
        } else {
            value = this.props.endOn;
        }

        // If the date is in a previous month, we need to disable older dates
        if (moment().startOf('month').isAfter(value)) {
            const beforeTodayRange = moment.range(
                value.clone().startOf('month'),
                moment().subtract(1, 'day')
            );

            for (const day of beforeTodayRange.by('days')) {
                if (day.isSame(value)) {
                    continue;
                }
                blackout.push(day.format(this.format));
            }
        }

        if (moment().isBefore(this.props.startOn) && this.props.inputType === 'end' && this.props.startOn) {
            // For the "end" input, we can't select a date before the startOn
            const selectedRange = moment.range(moment(), this.props.startOn);
            for (const day of selectedRange.by('days')) {
                blackout.push(day.format(this.format));
            }
        }

        return blackout;
    }

    /**
     * Returns true if a hover should be displayed for this date
     * @param  {Moment[]} selectedDates Dates currently selected
     * @param  {jQuery} $target the hovered span
     * @return {bool} Whether we should display a hover or not
     */
    shouldHoverDate(selectedDates, $target) {
        if (this.props.inputType === 'end' && $target.length > 0 && $target.attr('data-date')) {
            if ($target.hasClass('unselectable') || $target.hasClass('past')) {
                this.clearHover();
                return false;
            }

            const range = moment.range(this.props.startOn, moment($target.attr('data-date'), this.format));
            const overlapsBookedPeriod = CalendarUtils.overlapsBookedPeriod(range, this.props.availabilities);

            if (overlapsBookedPeriod) {
                this.clearHover();
                return false;
            }

            // Any other cases, show hover
            return true;
        }

        return false;
    }

    /**
     * Add classes to display hovered dates
     * @param {Moment} pickedDate The first date selected
     * @param {Moment} hoverDate The date currently hovered
     */
    addHover(pickedDate, hoverDate) {
        if (this.props.inputType === 'start') {
            // The start date never shows range, only the current hovered date
            return false;
        } else if (this.props.startOn && !this.props.endOn) {
            // If we have a selected startOn but no endOn, show hover between the start date and the hovered date
            super.addHover(this.props.startOn, hoverDate);
        }
    }

    /**
     * Override default function to also show selected range
     * @param {Object}   nextProps Next properties (after update)
     */
    getDateClassMap(nextProps) {
        const dateClassMap = super.getDateClassMap(nextProps);

        if (nextProps.startOn && nextProps.endOn) {
            const selectedRange = moment.range(nextProps.startOn, nextProps.endOn);

            for (const day of selectedRange.by('days')) {
                let previousClasses = dateClassMap[day.format(this.format)];
                if (!previousClasses) {
                    previousClasses = '';
                }

                if (day.isSame(nextProps.startOn)) {
                    dateClassMap[day.format(this.format)] = `${previousClasses} k-range-start`;
                } else if (day.isSame(nextProps.endOn)) {
                    dateClassMap[day.format(this.format)] = `${previousClasses} k-range-end`;
                } else {
                    dateClassMap[day.format(this.format)] = `${previousClasses} k-range`;
                }
            }
        }

        // If the date is in a previous month, we need to handle styling for older dates
        let value;
        if (this.props.inputType === 'start') {
            value = this.props.startOn;
        } else {
            value = this.props.endOn;
        }

        if (moment().startOf('month').isAfter(value)) {
            // We open a calendar with a date in a past month
            const beforeTodayRange = moment.range(value.startOf('month'), moment().subtract(1, 'day'));

            for (const day of beforeTodayRange.by('days')) {
                let previousClasses = dateClassMap[day.format(this.format)];
                if (!previousClasses) {
                    previousClasses = '';
                }
                dateClassMap[day.format(this.format)] = `${previousClasses} past`;
            }
        }

        if (nextProps.inputType === 'end' && nextProps.startOn) {
            // Add special style for start date
            let previousClasses = dateClassMap[nextProps.startOn.format(this.format)];

            if (!previousClasses) {
                previousClasses = '';
            }
            dateClassMap[nextProps.startOn.format(this.format)] = `${previousClasses} k-range-start`;

            if (moment().isBefore(nextProps.startOn)) {
                // Days between today and the start date are unselectable
                const selectedRange = moment.range(moment(), nextProps.startOn);
                for (const day of selectedRange.by('days')) {
                    previousClasses = dateClassMap[day.format(this.format)];
                    if (!previousClasses) {
                        previousClasses = '';
                    }
                    dateClassMap[day.format(this.format)] = `${previousClasses} past`;
                }
            }
        }

        return dateClassMap;
    }

    addCalendarLabel() {
        if (
            this.calendar &&
            this.calendar.container &&
            !this.calendar.container.querySelector('.editable-date-calendar-fullscreen-label')
        ) {
            const calendarLabel = document.createElement('div');
            if (this.props.inputType === 'start') {
                calendarLabel.innerHTML = i18n.t('exchange:start_on');
            } else if (this.props.inputType === 'end') {
                calendarLabel.innerHTML = i18n.t('exchange:end_on');
            }
            calendarLabel.setAttribute('class', 'editable-date-calendar-fullscreen-label');
            // Insert label as first child
            this.calendar.container.insertBefore(calendarLabel, this.calendar.container.firstChild);
        }
    }

    render() {
        return <input type="text" readOnly className="form-control field-date" ref={this.inputRef} />;
    }
}
